import {
  DatePicker,
  Dialog,
  SpinningLoader,
  useToast,
} from '@vallourec-ui/vallourec-ui'

import { useTranslation } from 'react-i18next'

import React, { useEffect, useRef, useState } from 'react'

import { isAxiosError } from 'axios'

import {
  ButtonContainer,
  ColumnContainer,
  Row,
  RowView,
  SearchBarContainer,
  SpinningContainer,
  StyledButton,
  StyledInputLabel,
  StyledTextButton,
} from './style'

import { SiteProps, TestProps } from '@/@types/global'
import { getFile } from '@/api/get'
import {
  document as documentPost,
  documentApproval as documentApprovalPost,
} from '@/api/post'
import { updateReportDocument } from '@/api/put'
import TextAreaInput from '@/components/TextAreaFieldInput'
import TextFieldInput from '@/components/TextFieldInput'
import { PdfViewer } from '@/components/common'
import { useApp } from '@/stores/AppStore'
import { useDocumentApproval } from '@/stores/DocumentApprovalStore'
import { TESTS } from '@/utils/constants'
import {
  downloadPdfFile,
  getFilePDF,
  getPDFBase64,
  getReportNumber,
} from '@/utils/pdfGenerator/pdfGenerator'
import {
  dealWithFile,
  downloadBase64,
  extractFileNameByPath,
  getDDWithAllSignature,
  getFilesByPaths,
  replaceInObject,
} from '@/utils/utils'

const ViewModal: React.FC<{
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onOutsideClick: () => void
  doc: any
}> = ({ isOpen, onOutsideClick, doc, setIsOpen }) => {
  const {
    templateRowResult,
    setTemplateRowResult,
    itemsResult,
    setItemsResult,
    setIsRealoadItemsResult,
  } = useDocumentApproval()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [comment, setComment] = useState<string>('')
  const [inspector, setInspector] = useState<string>('')
  const [datePublished, setDatePublished] = useState<Date | undefined>(
    undefined
  )
  const [base64DATA, setBase64DATA] = useState<string>('')
  const { user } = useApp()
  const [documentDefinitionApproval, setDocumentDefinitionApproval] =
    useState<any>(null)
  const pdfObjectUrlRef = useRef<string>('')

  const revokePdfObjectUrl = (value: string) => {
    if (value && value.startsWith('blob:')) {
      URL.revokeObjectURL(value)
    }
  }

  const createPdfObjectUrl = (base64: string) => {
    const byteCharacters = atob(base64)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024)
      const byteNumbers = new Array(slice.length)

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      byteArrays.push(new Uint8Array(byteNumbers))
    }

    return URL.createObjectURL(
      new Blob(byteArrays, { type: 'application/pdf' })
    )
  }

  const setPdfDataFromBase64 = (base64: string) => {
    const objectUrl = createPdfObjectUrl(base64)
    setBase64DATA((previousValue) => {
      revokePdfObjectUrl(previousValue)
      pdfObjectUrlRef.current = objectUrl
      return objectUrl
    })
  }

  const clearPdfData = () => {
    setBase64DATA((previousValue) => {
      revokePdfObjectUrl(previousValue)
      pdfObjectUrlRef.current = ''
      return ''
    })
  }

  const handleOutside = () => {
    onOutsideClick()
  }

  useEffect(() => {
    return () => {
      revokePdfObjectUrl(pdfObjectUrlRef.current)
      pdfObjectUrlRef.current = ''
    }
  }, [])

  useEffect(() => {
    async function fetchData() {
      try {
        if (isOpen) {
          setIsLoading(true)
          clearPdfData()

          const userLocalStorage = localStorage.getItem('cognito:user')
          let userData
          if (userLocalStorage && JSON.stringify(userLocalStorage) !== '{}') {
            userData = JSON.parse(userLocalStorage)
          }

          if (doc) {
            if (doc.path_file && doc.path_document_definition) {
              const reportFile = await getFilesByPaths({
                pathDD: doc.path_document_definition,
              })
              let documentDefinition = reportFile.dd

              if (documentDefinition && documentDefinition.content) {
                documentDefinition = await getDDWithAllSignature(
                  doc,
                  documentDefinition,
                  userData.email
                )

                const dd = JSON.parse(JSON.stringify(documentDefinition))
                dd.footer = function (currentPage: number, pageCount: number) {
                  return [
                    {
                      text: `${currentPage.toString()} / ${pageCount}`,
                      alignment: 'right',
                      margin: [0, 0, 30, 0],
                      style: 'small',
                    },
                    {
                      text: [
                        {
                          style: 'boldSmall',
                          text: 'Nº relatório (report n.): ',
                        },
                        {
                          style: 'small',
                          text: dd.content[0].table.body[1][2].text[1].text,
                        },
                      ],
                      alignment: 'left',
                      margin: [30, 0, 0, 0],
                    },
                  ]
                }
                setDocumentDefinitionApproval(dd)

                const stringFileBase64 = await getPDFBase64(
                  JSON.parse(JSON.stringify(dd))
                )
                setPdfDataFromBase64(stringFileBase64)
              }
            } else {
              const result = await documentPost({
                download: doc.document_id,
                viewDocument: true,
                email: userData.email,
              })
              if (result) {
                setPdfDataFromBase64(result.report.report)
              }
            }
          }
        }
      } catch (error: any) {
        console.log(error)
        throw new Error(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const enableButton = (action: string): boolean => {
    if (!doc) {
      return false
    }

    const actionPage = doc.action

    if (action === 'CHECKER') {
      return !!(
        doc.test_type === 'Corrosion' && actionPage === 'Aguardando revisão'
      )
    }
    if (action === 'APPROVE') {
      return actionPage === 'Aguardando aprovação'
    }
    if (action === 'REPROVE') {
      return !!(
        actionPage === 'Aguardando revisão' ||
        actionPage === 'Aguardando aprovação'
      )
    }
    if (action === 'STORE') {
      return actionPage === 'Aguardando publicação'
    }
    if (action === 'CANCEL') {
      return !!(
        actionPage === 'Aguardando revisão' ||
        actionPage === 'Aguardando aprovação' ||
        actionPage === 'Aguardando alteração'
      )
    }
    return true
  }

  const validatePermissionReport = (optionGenerate: string) => {
    const typeCode = doc.test_type

    let hasPermission = false
    if (user && user.permissions) {
      switch (optionGenerate) {
        case 'Publicado':
          hasPermission = true // item[0][typeCode.toLowerCase()] === 'publish';
          break
        case 'Aguardando alteração':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('check') ||
            user.permissions[typeCode.toLowerCase()].includes('approve')
          break
        case 'Aguardando aprovação':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('check')
          break
        case 'Aguardando publicação':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('approve')
          break
        case 'Cancelado':
          hasPermission =
            user.permissions[typeCode.toLowerCase()].includes('check') ||
            user.permissions[typeCode.toLowerCase()].includes('approve')
          break
        default:
          break
      }
    }

    return hasPermission
  }

  const validateSameUserReport = (optionGenerate: string) => {
    let hasPermission = true

    if (user) {
      switch (optionGenerate) {
        case 'Aguardando alteração':
          if (
            (doc.analyst_email === user.email &&
              doc.test_type !== 'Corrosion') ||
            doc.checker_email === user.email
          ) {
            hasPermission = false
          }
          break
        case 'Aguardando aprovação':
          if (doc.analyst_email === user.email) {
            hasPermission = false
          }
          break
        case 'Aguardando publicação':
          if (
            hasPermission &&
            ((doc.analyst_email === user.email &&
              doc.test_type !== 'Corrosion') ||
              doc.checker_email === user.email)
          ) {
            hasPermission = false
          }
          break
        default:
          break
      }
    }

    return hasPermission
  }

  const executeAction = async (actionToExecute: string) => {
    if (!user) {
      toast({
        title: 'Error',
        description: t('approval_flow.user_not_found'),
        type: 'error',
      })
      return
    }

    if (!user.hasSignature) {
      toast({
        title: 'Error',
        description: t('user.signature_not_exists'),
        type: 'error',
      })
      return
    }

    if (
      (actionToExecute === 'Cancelado' ||
        actionToExecute === 'Aguardando alteração') &&
      (!comment || !comment.trim().length)
    ) {
      toast({
        title: 'Error',
        description: t('approval_flow.comment_reqiured'),
        type: 'error',
      })
      return
    }
    if (
      ['Aguardando aprovação', 'Aguardando publicação'].includes(
        actionToExecute
      ) &&
      !user.hasSignature
    ) {
      toast({
        title: 'Error',
        description: t('approval_flow.signature_not_exists'),
        type: 'error',
      })
      return
    }

    if (!validatePermissionReport(actionToExecute)) {
      toast({
        title: 'Error',
        description: t('approval_flow.user_not_permission_generate_test'),
        type: 'error',
      })
      return
    }

    if (!validateSameUserReport(actionToExecute)) {
      toast({
        title: 'Error',
        description: t('approval_flow.same_user_generate_test'),
        type: 'error',
      })
      return
    }

    if (
      actionToExecute === 'Publicado' &&
      (!inspector || inspector === '' || !datePublished)
    ) {
      toast({
        title: 'Error',
        description: t('approval_flow.publishing_required'),
        type: 'error',
      })
      return
    }

    try {
      if (isOpen) {
        setIsLoading(true)
        if (doc) {
          const userLocalStorage = localStorage.getItem('cognito:user')
          let userData
          if (userLocalStorage && JSON.stringify(userLocalStorage) !== '{}') {
            userData = JSON.parse(userLocalStorage)
          }

          const testConfigured = TESTS.find(
            (item) =>
              item.name.toLowerCase() === doc.test_name.toLowerCase() ||
              item.namePt.toLowerCase() === doc.test_name.toLowerCase()
          )

          if (!testConfigured) {
            throw new Error(t('approval_flow.test_not_configured'))
          }

          if (!doc.test_type_number && testConfigured)
            doc.test_type_number = testConfigured.type

          let reportNumberValue
          let newDocumentDefinition
          let fileName
          let fileNameDD
          if (
            doc.path_file &&
            doc.path_document_definition &&
            documentDefinitionApproval
          ) {
            if (actionToExecute === 'Aguardando publicação') {
              reportNumberValue = await getReportNumber(
                { code: doc.site } as SiteProps,
                {
                  typeCode: testConfigured.typeCode,
                  type: testConfigured.type,
                } as TestProps,
                false
              )

              const dd = JSON.parse(JSON.stringify(documentDefinitionApproval))

              const numberTemporary =
                dd.content[0].table.body[1][2].text[1].text
              dd.content[0].table.body[1][2].text[1].text = reportNumberValue

              fileName = `${doc.test_name}_${reportNumberValue}.pdf`
              fileNameDD = fileName.replace('.pdf', '.json')

              if (numberTemporary && reportNumberValue) {
                newDocumentDefinition = replaceInObject(
                  dd,
                  numberTemporary,
                  reportNumberValue
                )
              }
            } else if (actionToExecute === 'Publicado' && doc.code_view) {
              reportNumberValue = doc.code_view
            }

            if (!newDocumentDefinition) {
              newDocumentDefinition = documentDefinitionApproval

              if (!fileName) {
                fileName = extractFileNameByPath(doc.path_document_definition)
                fileNameDD = fileName.replace('.pdf', '.json')
              }
            }

            const file = await getFilePDF(
              JSON.parse(JSON.stringify(newDocumentDefinition)),
              fileName ?? 'document.pdf'
            )

            const dataFile = await dealWithFile(
              file,
              `report/${testConfigured.id}/${doc.document_id}/${file.name}`,
              doc.document_id,
              'path_file'
            )

            await updateReportDocument(dataFile)

            const jsonDD = JSON.stringify(newDocumentDefinition, null, 2)
            const fileDD = new File(
              [new Blob([jsonDD], { type: 'text/plain' })],
              fileNameDD ?? 'document.json',
              { type: 'application/json' }
            )

            const dataFileDocumentDefinition = await dealWithFile(
              fileDD,
              `document_definition/${testConfigured.id}/${doc.document_id}/${fileDD.name}`,
              doc.document_id,
              'path_document_definition'
            )

            await updateReportDocument(dataFileDocumentDefinition)
          }

          const result = await documentApprovalPost({
            execAction: [doc],
            action: actionToExecute,
            comment,
            user: userData.email,
            userName: user.userName,
            date_published: datePublished,
            inspector,
            reportNumberNewValue: reportNumberValue,
            nameFileNew: fileName,
          })
          if (result) {
            if (itemsResult) {
              const updatedItems = itemsResult.map((el) => {
                const novoItem = result.items.find(
                  (item: any) => item.id === el.id
                )
                return novoItem || el
              })

              setItemsResult(updatedItems)
              setIsRealoadItemsResult(true)
            }

            toast({
              title: 'Success',
              description: t('application.commons.success.operation'),
              type: 'success',
            })

            try {
              if (actionToExecute === 'Aguardando publicação') {
                if (newDocumentDefinition) {
                  await downloadPdfFile(newDocumentDefinition, fileName)
                } else {
                  const resultDocument = await documentPost({
                    download: doc.document_id,
                  })
                  downloadBase64(
                    document,
                    resultDocument.report.name,
                    resultDocument.report.report,
                    atob(resultDocument.report.report)
                  )
                }
              }
            } catch (error) {
              console.log(error)
              if (isAxiosError(error)) {
                toast({
                  title: 'Error',
                  description:
                    error.response?.data?.errorMessage ?? error.message,
                  type: 'error',
                })
              } else {
                toast({
                  title: 'Error',
                  description: `${error}`,
                  type: 'error',
                })
              }
            }

            handleOutside()
          }
        }
      }
    } catch (error: any) {
      console.log(error)
      if (isAxiosError(error)) {
        const errorMsg =
          error.response?.data?.stack === 'Ação já realizada.'
            ? t('approval_flow.invalid_action')
            : undefined
        toast({
          title: 'Error',
          description:
            errorMsg ?? error.response?.data?.errorMessage ?? error.message,
          type: 'error',
        })
      } else {
        toast({
          title: 'Error',
          description: `${error}`,
          type: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog.Root
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(false)}
      closeOnInteractOutside={false}
      width="95%"
      header={<Dialog.Header />}
    >
      <div style={{ width: '100%' }}>
        {isLoading ? (
          <SpinningContainer>
            <SpinningLoader />
          </SpinningContainer>
        ) : (
          <>
            <Row>
              <ColumnContainer>
                {base64DATA && <PdfViewer base64DATA={base64DATA} />}
              </ColumnContainer>
            </Row>
            {doc && doc.action === 'Aguardando publicação' && (
              <Row style={{ width: '60%' }}>
                <SearchBarContainer>
                  <StyledInputLabel>
                    {' '}
                    {t<string>('searchReport.view.inspector')}{' '}
                  </StyledInputLabel>
                  <TextFieldInput
                    onChange={(event) => setInspector(event?.target?.value)}
                    placeholder={t('', t('searchReport.view.inspector'))}
                    value={inspector}
                  />
                </SearchBarContainer>
                <SearchBarContainer>
                  <DatePicker.Root>
                    <DatePicker.Trigger
                      label={t<string>('searchReport.view.datePublished')}
                      value={
                        datePublished ? new Date(datePublished) : undefined
                      }
                    />
                    <DatePicker.SingleDayPicker
                      onDaySelect={({ selected }) =>
                        selected && setDatePublished(selected)
                      }
                    />
                  </DatePicker.Root>
                </SearchBarContainer>
              </Row>
            )}
            {doc && doc.action != 'Publicado' && (
              <Row>
                <ColumnContainer>
                  <SearchBarContainer>
                    <StyledInputLabel>
                      {' '}
                      {t<string>('searchReport.view.comment')}{' '}
                    </StyledInputLabel>
                    <TextAreaInput
                      fontSize="text-sm"
                      name="actionNameInput"
                      disabled={false}
                      onChange={(event) => setComment(event?.target?.value)}
                      placeholder={t('', t('searchReport.view.comment'))}
                      width="100%"
                      minHeight={30}
                      value={comment}
                    />
                  </SearchBarContainer>
                </ColumnContainer>
              </Row>
            )}
            <RowView>
              {enableButton('STORE') && (
                <ButtonContainer>
                  <StyledButton onClick={() => executeAction('Publicado')}>
                    <StyledTextButton>
                      {t<string>('searchReport.view.button.publish')}
                    </StyledTextButton>
                  </StyledButton>
                </ButtonContainer>
              )}

              {enableButton('REPROVE') && (
                <ButtonContainer>
                  <StyledButton
                    onClick={() => executeAction('Aguardando alteração')}
                  >
                    <StyledTextButton>
                      {t<string>('searchReport.view.button.reject')}
                    </StyledTextButton>
                  </StyledButton>
                </ButtonContainer>
              )}

              {enableButton('APPROVE') && (
                <ButtonContainer>
                  <StyledButton
                    onClick={() => executeAction('Aguardando publicação')}
                  >
                    <StyledTextButton>
                      {t<string>('searchReport.view.button.approve')}
                    </StyledTextButton>
                  </StyledButton>
                </ButtonContainer>
              )}

              {enableButton('CHECKER') && (
                <ButtonContainer>
                  <StyledButton
                    onClick={() => executeAction('Aguardando aprovação')}
                  >
                    <StyledTextButton>
                      {t<string>('searchReport.view.button.check')}
                    </StyledTextButton>
                  </StyledButton>
                </ButtonContainer>
              )}

              {enableButton('CANCEL') && (
                <ButtonContainer>
                  <StyledButton onClick={() => executeAction('Cancelado')}>
                    <StyledTextButton>
                      {t<string>('searchReport.view.button.cancel')}
                    </StyledTextButton>
                  </StyledButton>
                </ButtonContainer>
              )}
            </RowView>
          </>
        )}
      </div>
    </Dialog.Root>
  )
}

export default ViewModal
